// LiveStreamCupidGiftEntry.jsx
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';

import getTranslationKey from '../resource/getTranslationKey.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { ButtonId } from '../resource/trackingConstants.js';
import { Game } from '../resource/gameConstants.js';
import { NULL_FUNCTION } from '../resource/defaults.js';
import { game as gameDebug } from '../resource/debug.js';

import media from '../style/media.js';
import { color, textColor } from '../style/variables.js';

import CupidGiftImageSource from '../../img/img-ugpp-cupid-gift.png';
import GuessGiftIconSource from '../../img/ic-guess-gift-white.svg';

const gameDebugLog = gameDebug.extend('log:component/LiveStreamCupidGiftEntry');

export const LiveStreamCupidGiftEntry = ({
  streamId = '',
  meId = null,
  isSelected = false,
  onSelect = NULL_FUNCTION,
  onEnterGame = NULL_FUNCTION,
  login = NULL_FUNCTION,
}) => {
  const { t } = useTranslation();
  const trackingPayload = useMemo(
    () => ({ 'stream.id': streamId }),
    [streamId]
  );
  const handleMakeWishClick = useCallback(
    async event => {
      event.stopPropagation();
      if (!meId) {
        try {
          await login();
        } catch (error) {
          gameDebugLog('login did not complete before entering game', {
            error,
            gameId: Game.UGPP_CUPID_GIFT,
            streamId,
          });
          return;
        }
      }
      onEnterGame({ streamId });
    },
    [login, meId, onEnterGame, streamId]
  );
  return (
    <BorderWrapper $isSelected={isSelected}>
      <StyledLiveStreamCupidGiftEntry
        data-element_id={ButtonId.LiveStream.ButtonUgppCupidGiftGame}
        data-tracking_payload={trackingPayload}
        $isSelected={isSelected}
        onClick={onSelect}
      >
        <EntryImageWrapper>
          <Glow />
          <EntryImage src={CupidGiftImageSource} alt="" />
        </EntryImageWrapper>
        <Badge>
          <img src={GuessGiftIconSource} alt="" width="12" height="12" />
        </Badge>
        <Title>
          {t(getTranslationKey({ key: Game.UGPP_CUPID_GIFT }), {
            ns: TranslationNamespace.LIVESTREAM,
          })}
        </Title>
        {isSelected && (
          <MakeWishButton
            role="button"
            tabIndex={0}
            data-element_id={ButtonId.LiveStream.ButtonUgppCupidGiftMakeWish}
            data-tracking_payload={trackingPayload}
            onClick={handleMakeWishClick}
          >
            {t('gift_make_wish', { ns: TranslationNamespace.GIFT })}
          </MakeWishButton>
        )}
      </StyledLiveStreamCupidGiftEntry>
    </BorderWrapper>
  );
};

LiveStreamCupidGiftEntry.propTypes = {
  streamId: PropTypes.string,
  meId: PropTypes.string,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func,
  onEnterGame: PropTypes.func,
  login: PropTypes.func,
};

const BorderWrapper = styled.div`
  max-width: 108px;
  width: 100%;
  height: 126px;
  box-sizing: border-box;
  padding: 1px;
  border-radius: 8px;
  background: ${({ $isSelected }) =>
    $isSelected ? color.giftSelectionGradient : 'transparent'};
`;

const breatheKeyframes = keyframes`
  0%, 100% {
    transform: scale(0.9);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.25);
    opacity: 1;
  }
`;

const heartbeatKeyframes = keyframes`
  0%, 30%, 60%, 100% {
    transform: scale(1);
  }
  15% {
    transform: scale(1.2);
  }
  45% {
    transform: scale(1.12);
  }
`;

const Glow = styled.div`
  position: absolute;
  top: calc(50% - 60px);
  left: calc(50% - 60px);
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: ${color.cupidGiftEntryGlow};
  animation: 3.2s ease-in-out infinite ${breatheKeyframes};
  pointer-events: none;
`;

const EntryImageWrapper = styled.div`
  position: relative;
  width: 72px;
  height: 72px;
`;

const EntryImage = styled.img`
  position: relative;
  width: 100%;
  height: 100%;
  object-fit: contain;
  animation: 1.6s ease-in-out infinite ${heartbeatKeyframes};
`;

const StyledLiveStreamCupidGiftEntry = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${({ $isSelected }) =>
    $isSelected ? 'space-between' : 'center'};
  gap: ${({ $isSelected }) => ($isSelected ? '2px' : '8px')};
  font-size: 12px;
  border-radius: 8px;
  box-sizing: border-box;
  overflow: hidden;
  cursor: pointer;
  background: ${({ $isSelected }) =>
    $isSelected
      ? `${color.giftSelectionOverlay} ${color.neutral[0]}`
      : 'transparent'};
`;

const Badge = styled.div`
  position: absolute;
  top: 4px;
  left: 4px;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background-color: ${color.neutral['20_alpha_80']};
`;

const Title = styled.div`
  position: relative;
  margin: 0;
  font-size: 10px;
  width: 100%;
  color: ${color.neutral[45]};
  text-align: center;
  line-height: 12px;
  ${media.tablet`
    font-size: 12px;
  `}
`;

const MakeWishButton = styled.div`
  position: relative;
  align-self: stretch;
  box-sizing: border-box;
  padding: 2px 12px;
  border-radius: 0px 0px 6px 6px;
  background: ${color.giftSelectionGradient};
  color: ${textColor.white};
  font-size: 12px;
  line-height: 16px;
  user-select: none;
  text-align: center;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

export default LiveStreamCupidGiftEntry;
